'use client';

import { ActionType } from '@diritto/common/app/inapp-event.constant';
import { HeaderNavMenu, WebViewScreen } from '@diritto/common/app/webview-header.constant';
import { EventJsonLd } from 'app-components/events/EventJsonLd';
import { EventView } from 'app-components/events/EventView';
import { AxiosError } from 'axios';
import { useInAppState } from 'contexts/InAppProvider';
import { usePathname } from 'next/navigation';
import { useGetEventQuery } from 'queries/events/useGetEventQuery';
import React, { useCallback, useEffect } from 'react';
import { FullPageLoading } from 'ui-components/common/Loading';
import { ErrorStatusView } from 'ui-components/error/ErrorStatusView';
import { MutableHeaderLayout } from 'ui-components/layout/MutableHeaderLayout';
import { ShareButton } from 'ui-components/navigations/HeaderButtons';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';
import { useShare } from 'ui-hooks/useShare';
import { configure } from 'utils/configure';

interface EventPageProps {
  eventId: number;
  ignoreAccessible?: boolean;
  isApp?: boolean;
}

export const EventPage: React.FC<EventPageProps> = ({ eventId, isApp, ignoreAccessible = false }) => {
  useInAppHeader({
    screen: WebViewScreen.EventDetail,
    rightMenuList: [HeaderNavMenu.Share],
  });
  useInAppStatusBar(WebViewScreen.EventDetail);

  const pathname = usePathname();
  const thisUrl = `${configure.serviceUrl}${pathname}`;

  const { data, error, isLoading } = useGetEventQuery(eventId, ignoreAccessible);

  const { addActionEventListener } = useInAppState();
  const handleShare = useShare();

  const shareEvent = useCallback(() => {
    if (data?.event) {
      handleShare({ title: data.event.title, text: data.event.title, url: thisUrl });
    }
  }, [data?.event, handleShare, thisUrl]);

  useEffect(() => {
    const listener = (params: { type: ActionType }) => {
      if (params.type === ActionType.Share) {
        shareEvent();
      }
    };
    addActionEventListener(listener);
    return () => {
      // 이벤트 리스너 정리
      addActionEventListener(listener);
    };
  }, [addActionEventListener, shareEvent]);

  if (error) {
    return (
      <MutableHeaderLayout title="이벤트" isApp={isApp}>
        <ErrorStatusView statusCode={(error as AxiosError)?.response?.status} />
      </MutableHeaderLayout>
    );
  }

  if (isLoading || !data) {
    return (
      <MutableHeaderLayout title="이벤트" isApp={isApp}>
        <FullPageLoading />
      </MutableHeaderLayout>
    );
  }

  return (
    <>
      <EventJsonLd event={data.event} />
      <MutableHeaderLayout
        showTopUp
        isApp={isApp}
        title="이벤트"
        rightButtons={[
          <ShareButton
            key="share"
            params={{
              url: thisUrl,
              title: data.event.title,
              text: data.event.title,
            }}
          />,
        ]}>
        <EventView eventData={data} />
      </MutableHeaderLayout>
    </>
  );
};
